import { useState } from "react";

import { Models, OrderPickupStatus } from "api/types";

export const filterItemsByProducts = <T extends { id: string }>(
  items: T[],
  productIds: string[]
): T[] => {
  return items.filter((item) => productIds.includes(item.id));
};

export const generateShopUrl = (
  view: string,
  params?: Record<string, string>
) => {
  const searchParams = new URLSearchParams({ view, ...params });
  return `/products/list?${searchParams.toString()}`;
};

export const normalizeItemName = (item: {
  id: string;
  name?: string | null;
}): { id: string; name: string } => ({
  id: item.id,
  name: item.name ?? "",
});

export const getProductIds = (
  products: Models<"DisplayRetailProductListing">[],
  field: keyof Models<"DisplayRetailProductListing">
): string[] => {
  return products.flatMap((product) => {
    const value = product[field];
    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === "string");
    }
    return typeof value === "string" ? [value] : [];
  });
};

export const isBestSeller = (
  product: Models<"DisplayRetailProductListing">,
  user: any
) => {
  return user?.isGuest
    ? "Members Only"
    : product.isBestSeller
    ? "Best Seller"
    : "";
};

export const isFeatured = (product: Models<"DisplayRetailProductListing">) => {
  return product.isFeaturedProduct ? "Featured" : "";
};

export const shouldShowAdonisCTA = (
  index: number,
  isAdonisEnabled: boolean
): boolean => {
  return index % 4 === 3 && index % 8 === 3 && isAdonisEnabled;
};

export const shouldShowAffirmBanner = (
  index: number,
  dunningMembership: any,
  platform: any
): boolean => {
  return (
    index % 4 === 3 &&
    index % 8 === 7 &&
    dunningMembership.status === false &&
    !!platform.platformSettings?.affirmEnabled
  );
};

export const useExpandedSections = (initialSection?: string | null) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    initialSection ? new Set([initialSection]) : new Set()
  );

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      newSet.has(sectionId) ? newSet.delete(sectionId) : newSet.add(sectionId);
      return newSet;
    });
  };

  return { expandedSections, toggleSection };
};

export interface ProgressInfo {
  current: number;
  max: number;
  statusText: string;
}

const FIVE_DAYS_IN_MS = 5 * 24 * 60 * 60 * 1000;

// TODO: Replace this to pull from aftership.
const isOrderWithin5Days = (createdOnUtc: string): boolean => {
  const now = new Date();
  const orderCreatedDate = new Date(createdOnUtc);
  const daysSinceOrder = now.getTime() - orderCreatedDate.getTime();
  return daysSinceOrder < FIVE_DAYS_IN_MS;
};

const hasUncompletedPickupItems = (
  pickupItems: Models<"DisplayRetailProductOrderLineItemForPickup">[]
): boolean => {
  return pickupItems.some(
    (lineItem) =>
      (lineItem.status as OrderPickupStatus) !== OrderPickupStatus.PickedUp
  );
};

const isUpcomingOrder = (
  order: Models<"DisplayRetailProductOrderFromPerson">
): boolean => {
  const hasPickupItems = order.lineItemsForPickup.length > 0;
  const hasShippingItems = order.lineItemsForShipping.length > 0;
  const isMixedOrder = hasPickupItems && hasShippingItems;

  const hasUncompleted = hasUncompletedPickupItems(order.lineItemsForPickup);
  const isWithin5Days = isOrderWithin5Days(order.createdOnUtc);

  // Mixed order: show if there's an uncompleted pickup OR within 5 days
  if (isMixedOrder) {
    return hasUncompleted || isWithin5Days;
  }

  // Pickup-only: show only if item hasn't been picked up
  if (hasPickupItems) {
    return hasUncompleted;
  }

  // Delivery-only: show if within 5 days of order
  if (hasShippingItems) {
    return isWithin5Days;
  }

  return false;
};

export const getUpcomingOrders = (
  orders: Models<"DisplayRetailProductOrderFromPerson">[]
) => {
  return orders.filter(isUpcomingOrder);
};

export const getPastOrders = (
  orders: Models<"DisplayRetailProductOrderFromPerson">[]
) => {
  return orders.filter((order) => !isUpcomingOrder(order));
};

export const getPastOrderItems = (
  orders: Models<"DisplayRetailProductOrderFromPerson">[]
) => {
  return orders
    .flatMap((order) => {
      const hasShippingItems = order.lineItemsForShipping.length > 0;
      const isWithin5Days = isOrderWithin5Days(order.createdOnUtc);

      const items = [];

      // Add completed pickup items
      items.push(
        ...order.lineItemsForPickup
          .filter(
            (lineItem) =>
              (lineItem.status as OrderPickupStatus) ===
              OrderPickupStatus.PickedUp
          )
          .map((lineItem) => ({
            orderId: order.orderID,
            completionDate: lineItem.lastRedeemedOnUtc,
            lineItem,
            lastRedeemedOnUtc: lineItem.lastRedeemedOnUtc,
            estimatedDeliveryDateAtUtc: undefined,
            shipmentTrackingUrl: undefined,
          }))
      );

      // Add shipping items for delivery/mixed orders that are past 5 days
      if (hasShippingItems && !isWithin5Days) {
        items.push(
          ...order.lineItemsForShipping.map((lineItem) => ({
            orderId: order.orderID,
            completionDate: lineItem.estimatedDeliveryDateAtUtc,
            lineItem,
            lastRedeemedOnUtc: undefined,
            estimatedDeliveryDateAtUtc: lineItem.estimatedDeliveryDateAtUtc,
            shipmentTrackingUrl: lineItem.shipmentTrackingUrl,
          }))
        );
      }

      return items;
    })
    .sort((a, b) => {
      const dateA = a.completionDate ? new Date(a.completionDate).getTime() : 0;
      const dateB = b.completionDate ? new Date(b.completionDate).getTime() : 0;
      return dateB - dateA; // Most recent first
    });
};

export const getPickupProgress = (
  pickupItems: Models<"DisplayRetailProductOrderLineItemForPickup">[]
): ProgressInfo => {
  if (pickupItems.length === 0) {
    return { current: 0, max: 2, statusText: "No items" };
  }

  const statusText = "Ready for you!";
  return {
    current: 2,
    max: 2,
    statusText,
  };
};

export const getDeliveryProgress = (
  shippingItems: Models<"DisplayRetailProductOrderLineItemForShipping">[]
): ProgressInfo => {
  if (shippingItems.length === 0) {
    return { current: 0, max: 2, statusText: "No items" };
  }

  // Determine status based on tracking URL availability
  const hasTrackingUrl = shippingItems[0].shipmentTrackingUrl != null;
  const statusInfo = hasTrackingUrl
    ? {
        current: 2,
        text: "Headed your way!",
      }
    : {
        current: 1,
        text: "Processing your order",
      };

  return {
    current: Math.max(statusInfo.current, 1),
    max: 2,
    statusText: statusInfo.text,
  };
};

export const hasProducts = (items: any[]) => items?.length > 0;

export const hasFeatured = (
  products: Models<"DisplayRetailProductListing">[]
) => products.some((product) => product.isFeaturedProduct);

export const hasBestSellers = (
  products: Models<"DisplayRetailProductListing">[]
) => products.some((product) => product.isFeaturedProduct); // TODO: change to isBestSeller

export const hasNewArrivals = (
  products: Models<"DisplayRetailProductListing">[]
) => {
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  return (
    products.filter((product) => {
      const productDate = new Date(product.createdAtUtc);
      return productDate >= thirtyDaysAgo;
    }).length > 0
  );
};

export const getRedeemedEarnedProducts = (
  earnedProducts: Models<"DisplayRetailProductOrderLineItemForPickup">[]
) => {
  return earnedProducts
    .filter((product) => product.status === OrderPickupStatus.PickedUp)
    .map((product) => ({
      orderId: undefined, // No order ID for earned products
      completionDate: product.lastRedeemedOnUtc,
      lineItem: product,
      lastRedeemedOnUtc: product.lastRedeemedOnUtc,
      estimatedDeliveryDateAtUtc: undefined,
      shipmentTrackingUrl: undefined,
    }))
    .sort((a, b) => {
      const dateA = a.completionDate ? new Date(a.completionDate).getTime() : 0;
      const dateB = b.completionDate ? new Date(b.completionDate).getTime() : 0;
      return dateB - dateA; // Most recent first
    });
};
